import React, { useCallback, useMemo, useState } from 'react';

import { NotificationManager, prepareRequest, useLabel } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input, Label, Select } from '@atlas/ui';

import { DIRECTA_BANKS } from 'common/constants';
import PaymentListItem from 'components/ClientProfile/Payment/PaymentListItem';

const optionsBanks = Object.entries(DIRECTA_BANKS).map(([key, value]) => ({
  text: value,
  value: key,
}));

const validateAccountNumber = (accountNumber, value) => {
  const maxLength = 10;
  const minLength = 9;
  if (!value) return { message: 'required' };

  if (value.length < 9) {
    return { message: 'min_length', params: { ruleOption: minLength } };
  } else if (value.length > 10) {
    return { message: 'max_length', params: { ruleOption: maxLength } };
  } else {
    return null;
  }
};

export const WithdrawalDirectaPopup = ({ data, id }) => {
  const { formAction, paymentMethod, pmId, redirectUrl } = data;
  const parsedData = useMemo(
    () => parseWithdrawalQuery(redirectUrl, { parseNumbers: false }),
    [redirectUrl],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const handleChangeBank = useCallback(value => {
    setBankCode(value);
  }, []);

  const handleChangeAccountNumber = useCallback(
    e => {
      const value = e.target.value.replace(/\D/g, '');

      const error = validateAccountNumber(accountNumber, value);

      setError(error);
      setAccountNumber(value);
    },
    [accountNumber],
  );

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;
    const request = prepareRequest({
      method: 'POST',
      url: formAction,
    });

    const { amount, currency, paymentId } = parsedData;

    const requestData = {
      additionalParameters: {
        bankAccount: accountNumber,
        bankCode,
        publicPaymentId: paymentId,
      },
      amount,
      currency,
      pmId,
    };

    setIsSubmitting(true);

    try {
      const response = await request(requestData);
      NotificationManager.display({
        data: {
          amount,
          currency,
          publicPaymentId: response?.publicPaymentId,
          paymentMethod,
        },
        message: 'successfulWithdrawal',
        type: 'successfulWithdrawal',
      });
    } catch (error) {
      NotificationManager.display({
        data: {
          errorType: true,
          headerTitle: 'withdrawal',
          text: 'withdrawal_directa24_text_error',
          title: 'failed_sad_emoticon',
        },
        type: 'withdrawalResultPopup',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    formAction,
    parsedData,
    accountNumber,
    bankCode,
    pmId,
    paymentMethod,
    handleClose,
  ]);

  return (
    <DefaultPopup
      className="payment-popup fullscreen"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="payment-popup-content">
        <div className="payment-popup-payment-method">
          <PaymentListItem
            amount={parseInt(parsedData.amount)}
            payment={{ ...paymentMethod, name: useLabel('directa24_title') }}
            showAmount
          />
        </div>

        <div className="payment-popup-block">
          <Input
            active={isActiveInput}
            className="payment-popup-input"
            error={error}
            inputMode="numeric"
            label="account_number"
            onBlur={() => setIsActiveInput(false)}
            onChange={handleChangeAccountNumber}
            onFocus={() => setIsActiveInput(true)}
            type="number"
            value={accountNumber}
          />

          {error && (
            <div className="help-text-container">
              <Label message={error.message} params={error.params} />
            </div>
          )}
        </div>

        <Select
          active={isActiveSelect}
          className="payment-popup-select"
          label="bank_code"
          onBlur={() => setIsActiveSelect(false)}
          onChange={handleChangeBank}
          onFocus={() => setIsActiveSelect(true)}
          options={optionsBanks}
          translateOptions
        />

        <Button
          className="payment-popup-button"
          disabled={!accountNumber || error || isSubmitting}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};
